// An upload refused for room (RFC-054 §6, byj-1wv.71.5): past the person's
// quota — by how much, what to delete, that an admin can raise it, their
// largest files when the page has them — or past Share's total, which is
// nothing of theirs and an admin's to make room for.
import { ApiError, type ShareFile } from "../api";
import { t } from "../i18n";
import { Link } from "./Nav";
import { fileBytes } from "../files";

/** Why an upload of `bytes` was refused for room, if it was. */
export type Refusal = { kind: "quota"; used: number; limit: number; bytes: number } | { kind: "total" };

export function refusalOf(err: unknown, bytes: number): Refusal | null {
  if (!(err instanceof ApiError) || err.status !== 413 || !err.body || typeof err.body !== "object") return null;
  const b = err.body as { code?: unknown; used?: unknown; limit?: unknown };
  if (b.code === "total") return { kind: "total" };
  if (b.code === "quota" && typeof b.used === "number" && typeof b.limit === "number") return { kind: "quota", used: b.used, limit: b.limit, bytes };
  return null;
}

/** The note for a refusal; with `mine`, their three largest files to delete from. */
export function Refused({ r, mine }: { r: Refusal; mine?: ShareFile[] }) {
  if (r.kind === "total") {
    return (
      <div className="card pad stack tight refused" role="alert" data-testid="refused-total">
        <strong>{t("files:refusedTotal")}</strong>
        <span className="small">{t("files:refusedTotalLead")}</span>
      </div>
    );
  }
  const over = Math.max(0, r.used + r.bytes - r.limit);
  const largest = (mine ?? [])
    .filter((f) => f.latest > 0)
    .sort((a, b) => b.bytes - a.bytes)
    .slice(0, 3);
  return (
    <div className="card pad stack tight refused" role="alert" data-testid="refused-quota">
      <strong>{t("files:refusedQuota", { over: fileBytes(over) })}</strong>
      <span className="small">{t("files:refusedQuotaLead", { bytes: fileBytes(r.bytes), used: fileBytes(r.used), limit: fileBytes(r.limit) })}</span>
      {largest.length > 0 ? (
        <>
          <span className="small">{t("files:refusedLargest")}</span>
          <ul className="small refused-largest">
            {largest.map((f) => (
              <li key={f.id}>
                <Link to={`/f/${encodeURIComponent(f.id)}`}>{f.name}</Link> <span className="muted">{t("files:refusedFileSize", { bytes: fileBytes(f.bytes), latest: f.latest })}</span>
              </li>
            ))}
          </ul>
        </>
      ) : (
        mine === undefined && (
          <span className="small">
            <Link to="/files">{t("files:refusedYourFiles")}</Link>
          </span>
        )
      )}
    </div>
  );
}
